import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { BiEdit } from "react-icons/bi";
import ReactLoading from "react-loading";
import { toast } from "react-toastify";
import {
  fetchAllTournaments,
  updateTournamentBanner,
  getTournamentById,
} from "../../../utils/services/tournament.services";
import EditTournamentModal from "../../../components/EditTournamentModal";

const TournamentManagement = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNoDataText, setShowNoDataText] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, [pageNumber, pageSize]);

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const response = await fetchAllTournaments(pageNumber, pageSize);

      const tournamentsArray = response?.data || [];
      const mappedData = tournamentsArray?.map((tournament) => ({
        id: tournament.id,
        tournamentName: tournament.name || "-",
        imageUrl: tournament.tournamentBanner || null,
        ownerName: tournament.ownerName || "-",
        tournamentType: tournament.tournamentType || "-",
        sport: tournament.sport || "-",
        startDate: tournament.startDate,
        endDate: tournament.endDate,
        startTime: tournament.startTime,
        endTime: tournament.endTime,
      }));

      setData(mappedData);
      setTotalRowCount(response?.length || 0);
      setShowNoDataText(mappedData.length === 0);
    } catch (error) {
      console.error("Error fetching tournaments:", error);
      setData([]);
      setTotalRowCount(0);
      setShowNoDataText(true);
    }
    setLoading(false);
  };

  const handleEdit = async (row) => {
    try {
      const tournamentDetails = await getTournamentById(row.id);

      const mappedTournament = {
        id: tournamentDetails.id || row.id,
        tournamentName: tournamentDetails.name || row.tournamentName,
        imageUrl: tournamentDetails.tournamentBanner || row.imageUrl,
        ownerName: tournamentDetails.ownerName || row.ownerName,
        tournamentType: tournamentDetails.tournamentType || row.tournamentType,
        sport: tournamentDetails.sport || row.sport,
      };

      setSelectedTournament(mappedTournament);
      document.body.classList.add("modal-open");
      setShowEditModal(true);
    } catch (error) {
      console.error("Error fetching tournament details:", error);
      setSelectedTournament(row);
      document.body.classList.add("modal-open");
      setShowEditModal(true);
    }
  };

  const hideEditModal = () => {
    document.body.classList.remove("modal-open");
    setShowEditModal(false);
    setSelectedTournament(null);
  };

  const handleUpdateImage = async (selectedFile) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("banner", selectedFile);

      await updateTournamentBanner(selectedTournament.id, formData);

      // Refresh the tournament list to get updated data
      await fetchTournaments();

      toast.success("Tournament banner updated successfully");
      hideEditModal();
    } catch (error) {
      console.error("Error updating tournament banner:", error);
      // Error toast is already shown in the service
    } finally {
      setIsSubmitting(false);
    }
  };

  const paginationOptions = {
    rowsPerPageText: "Items Per Page",
    rangeSeparatorText: "of",
    selectAllRowsItem: true,
    selectAllRowsItemText: "All",
  };

  const columns = [
    {
      name: "Tournament Name",
      selector: (row) => row.tournamentName || "-",
      sortable: true,
    },
    {
      name: "Image",
      cell: (row) => {
        if (row.imageUrl) {
          return (
            <img
              src={row.imageUrl}
              alt={row.tournamentName || "Tournament"}
              className="h-16 w-16 object-cover rounded"
            />
          );
        }
        return <span className="text-gray-500">No Image</span>;
      },
      sortable: false,
    },
    {
      name: "Owner Name",
      selector: (row) => row.ownerName || "-",
      sortable: true,
    },
    {
      name: "Tournament Type",
      selector: (row) => row.tournamentType || "-",
      sortable: true,
    },
    {
      name: "Sport",
      selector: (row) => row.sport || "-",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => handleEdit(row)}
              className="bg-theme cursor-pointer rounded px-4 py-2 font-bold text-white hover:bg-blue-700 h-10 mx-auto"
            >
              <BiEdit />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="mt-3">
      {/* Edit Modal */}
      <EditTournamentModal
        isOpen={showEditModal}
        tournament={selectedTournament}
        onClose={hideEditModal}
        onUpdate={handleUpdateImage}
        isSubmitting={isSubmitting}
      />

      <div className="mt-3 overflow-x-auto rounded-lg bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={data}
          pagination
          progressPending={loading}
          paginationComponentOptions={paginationOptions}
          progressComponent={
            <ReactLoading
              type={"bubbles"}
              color={"#4318FF"}
              height={100}
              width={100}
            />
          }
          noDataComponent={
            showNoDataText ? (
              <div className="py-8 text-center text-gray-500">
                There are no records to display
              </div>
            ) : (
              <ReactLoading
                type={"bubbles"}
                color={"#4318FF"}
                height={100}
                width={100}
              />
            )
          }
          onChangePage={(value) => {
            setPageNumber(value);
          }}
          paginationServer
          onChangeRowsPerPage={(value) => {
            setPageSize(value);
            setPageNumber(1);
          }}
          paginationTotalRows={totalRowCount}
          paginationDefaultPage={pageNumber}
          customStyles={{
            rows: {
              style: {
                minHeight: "2.5rem",
              },
            },
            headCells: {
              style: {
                fontSize: "0.9rem",
                backgroundColor: "#4318FF",
                fontWeight: "bold",
                letterSpacing: "0.05rem",
                color: "#FFFFFF",
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
              },
            },
            cells: {
              style: {
                fontSize: "0.9rem",
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
              },
            },
            pagination: {
              style: {
                borderTop: "1px solid #e5e7eb",
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default TournamentManagement;